import moment from 'moment';
import isBoolean from 'lodash/isBoolean';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useUser from '../../hooks/useUser';
import {getDefaultCase, formatCaseName} from '../../helpers/cases';
import { useCreateAppointmentMutation } from '../../redux/services/appointmentApi';
import { useGetSessionTypesQuery } from '../../redux/services/sessionTypeApi';
import AddressField from '../common/AddressField';
import CustomInputLabel from '../common/CustomInputLabel';
import CustomNumberField from '../common/CustomNumberField';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import FindCaseInput from '../cases/FindCaseInput';
import FindPatientForm from '../patients/FindPatientForm';
import LoadingScreen from '../common/LoadingScreen';
import TimeField from '../common/TimeField';
import DateField from '../common/DateField';
import { useGetCasesQuery } from '../../redux/services/caseApi';

const getDefaultFormValues = (props, cases) => ({
  patientId: props?.patient?.id,
  sessionTypeId: props?.sessionType?.id,
  date: moment().format('MM/DD/yyyy'),
  // date: moment(),
  time: '09:00 AM',
  duration: props?.sessionType?.duration_minutes,
  send_patient_email: !!props?.sessionType?.send_patient_email,
  send_provider_email: !!props?.sessionType?.send_provider_email,
  carryforward_only_my_own_notes: !!props?.sessionType?.carryforward_only_my_own_notes,
  instructions: props?.sessionType?.instructions,
  case_id: props.defaultCase ? {value: props.defaultCase.id, label: formatCaseName(props.defaultCase)} : null,
});

const parseBodyFromData = ({formValues}) => {
  let body = {
    patient_id: formValues.patientId, 
    session_type_id: formValues.sessionTypeId,
    start_time: moment(
      `${formValues.date} ${formValues.time}`,
      'MM/DD/yyyy hh:mm a'
    ).toISOString(),
    duration: formValues.duration,
    override_schedule: true,
    send_patient_email: formValues.send_patient_email,
    send_provider_email: formValues.send_provider_email,
    carryforward_only_my_own_notes: formValues.carryforward_only_my_own_notes,
    instructions: formValues.instructions,
  };
  if (formValues?.case_id) body.case_id = formValues.case_id?.value || formValues?.case_id;
  if (formValues.address_line_1) {
    body.address_line_1 = formValues.address_line_1;
    if (formValues.address_line_2) body.address_line_2 = formValues.address_line_2;
    body.address_city = formValues.address_city;
    body.address_state = formValues.address_state;
    body.address_zip = formValues.address_zip;
  }
  if (formValues.override_session_category) {
    body.override_session_category = formValues.override_session_category;
  }
  return body;
};

const handleFieldUpdate = ({
  formValues, 
  setFormValues, 
  setSuccess,
  onPatientIdChange,
}) => fieldName => e => {
  setSuccess(false);
  let value = e?.target?.value;
  if ([
    'send_patient_email', 
    'send_provider_email',
    'carryforward_only_my_own_notes',
  ].includes(fieldName)) {
    value = e?.target?.checked;
  }
  // if sessionType id is changed, reset address fields
  const omitFields = (
    fieldName === 'sessionTypeId'
    ? ['address_line_1', 'address_line_2', 'address_city', 'address_state', 'address_zip']
    : []
  );
  if (fieldName === 'case_id') console.log('CASE_VAL', value);
  let values = omit(
    fieldName === 'address'
    ? {
      ...formValues,
      address_line_1: value?.address_line_1,
      address_line_2: value?.address_line_2,
      address_city: value?.address_city,
      address_state: value?.address_state,
      address_zip: value?.address_zip,
    }
    : {
      ...formValues,
      [fieldName]: value,
    }
  , omitFields);
  if (fieldName === 'patientId') onPatientIdChange(e, value);
  return setFormValues(values);
};

const handleReset = ({props, setFormValues}) => () => {
  // const newFormValues = getDefaultFormValues(props);
  // setFormValues(newFormValues);
};

const handleSubmit = ({
  post, 
  saving, 
  setSaving, 
  setError,
  setSuccess,
  onReset,
  onSaved, 
}) => async (formValues, resetForm = false) => {
  if (saving) return;
  setSaving(true);
  setSuccess(false);
  const body = parseBodyFromData({ formValues });
  const response = await post(body);
  if (response.error) {
    return setError(new Error(response?.error?.data?.errors?.[0] || 'An error occurred'));
  }
  setSuccess(true);
  setError(null);
  setSaving(false);
  if (resetForm) return onReset();
  onSaved(response?.data?.id);
};

const NewSessionForm = props => {
  const {user, defaultCase, providerId, sessionTypes, onPatientIdChange} = props;
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState(getDefaultFormValues(props));
  const [post, lastResponse] = useCreateAppointmentMutation();
  const patientId = formValues?.patientId;
  const onFieldChange = handleFieldUpdate({formValues, setFormValues, setSuccess, onPatientIdChange});
  const onSaved = id => navigate(`/appointments/success/${id}`);
  const onReset = handleReset({props, setFormValues});
  const onSubmit = handleSubmit({
    post,
    saving,
    setSaving,
    setError,
    setSuccess,
    onReset,
    onSaved,
  });
  const lastResponseDate = lastResponse?.data?.session?.date;
  if (success) console.log('lastResponse', lastResponse);
  return (
    <Grid container spacing={1} padding={2}>
      <Grid item xs={12}>
        <Typography variant='h2'>Override Session Type Settings</Typography>
      </Grid>
      {error && <Alert severity='error'>{error.message}</Alert>}
      {success && lastResponse &&
        <Grid item xs={12}>
          <Alert severity='success'>
            Session created successfully for {moment(lastResponseDate).format('M/DD/yyyy')}.
          </Alert>
        </Grid>
      }
      <Grid item xs={12} md={6}>
        {props.sessionType?.id && sessionTypes.length && <CustomSelect
          label='Session Type'
          required
          defaultValue={
            props.sessionType?.id 
            ? props.sessionType.id
            : null
          }
          disableClearable={true}
          fullWidth
          options={sessionTypes.map(s => ({
            value: s.id,
            label: s.title,
          }))}
          onChange={onFieldChange('sessionTypeId')}
        />}
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomInputLabel>Selected Patient</CustomInputLabel>
        <FindPatientForm
          allPatients={true}
          defaultValue={props?.patient.id}
          onSubmit={({patientId}) => onFieldChange('patientId')({target: {value: patientId}})}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomSelect
          label='Category'
          required
          defaultValue={
            isBoolean(props?.sessionType?.is_physical_therapy)
            ? (props.sessionType.is_physical_therapy ? 'restore' : 'optimize')
            : null
          }
          fullWidth
          disableClearable
          onChange={onFieldChange('override_session_category')}
          options={[
            {value: 'restore', label: 'Restore'},
            {value: 'optimize', label: 'Optimize'},
          ]}
        />
      </Grid>
      <Grid item xs={0} md={6}>
        <CustomNumberField 
          label='Duration'
          required
          defaultValue={props?.sessionType?.duration_minutes || 30}
          fullWidth
          disableClearable
          min={0} 
          max={1000}
          step={1}
          maxLength={3}
          onChange={onFieldChange('duration')}
        />
      </Grid>
      <Grid item xs={6}>
        <FormGroup>
          <FormControlLabel
            id='session-carryforward_only_my_own_notes-checkbox'
            required={false}
            label='Carry forward only my own session notes'
            control={
              <Checkbox
                defaultChecked={!!props?.sessionType?.carryforward_only_my_own_notes}
                onChange={e => 
                  onFieldChange('carryforward_only_my_own_notes')(e)
                }
                sx={{
                  color: 'text.gray',
                }}
              />
            }
          />
        </FormGroup>
      </Grid>
      <Grid item xs={0} md={6}>
        <FindCaseInput 
          label='Case'
          fullWidth
          required
          allowCreate={true}
          allowEdit={true}
          useDefaultValue={false}
          defaultValue={props?.defaultCase?.id}
          patientId={formValues.patientId}
          onSubmit={onFieldChange('case_id')}
          onChange={onFieldChange('case_id')}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Stack paddingTop={0.15}>
          <DateField
            label='Date'
            required
            defaultValue={formValues.date}
            onChange={onFieldChange('date')}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box>
          <TimeField 
            label='Time'
            required={true}
            onChange={onFieldChange('time')}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <CustomTextField 
          inputType='textarea'
          label='Instructions'
          rows={2}
          multiline={true}
          fullWidth={true}
          defaultValue={props?.sessionType?.instructions}
          onChange={onFieldChange('instructions')}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormGroup>
          <FormControlLabel
            id='session-send_patient_email-checkbox'
            required={false}
            control={
              <Checkbox
                defaultChecked={!!props?.sessionType?.send_patient_email}
                onChange={e => 
                  onFieldChange('send_patient_email')(e)
                }
                sx={{
                  color: 'text.gray',
                }}
              />
            }
            label='Send the patient a calendar invitation'
          />
          {/* <FormHelperText>
            <Typography
              variant='body1'
              color='text.primary'

            >
              {childProps.helperText}
            </Typography>
          </FormHelperText> */}
        </FormGroup>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormGroup>
          <FormControlLabel
            id='session-send_provider_email-checkbox'
            required={false}
            control={
              <Checkbox
                defaultChecked={!!props?.sessionType?.send_provider_email}
                onChange={e => 
                  onFieldChange('send_provider_email')(e)
                }
                sx={{
                  color: 'text.gray',
                }}
              />
            }
            label='Send me a calendar invitation'
          />
          {/* <FormHelperText>
            <Typography
              variant='body1'
              color='text.primary'

            >
              {childProps.helperText}
            </Typography>
          </FormHelperText> */}
        </FormGroup>
      </Grid>
      <Grid item xs={12} md={6}>
        {(sessionTypes || []).find(s => s.id === formValues.sessionTypeId)?.location_type === 'OTHER_MOBILE' ? 
          <AddressField 
            showLabel
            fullWidth={true}
            onChange={address => onFieldChange('address')({target: {value: address}})}
          />
          : null
        }
      </Grid>
      <Grid item xs={12}>
        <Alert severity='warning'>
            Warning: This will override your schedule settings including scheduling limitations and away time.
            It is possible to double-book appointments.
        </Alert>
      </Grid>
      <Grid item xs={12}>
        <Stack direction='row' justifyContent='center' spacing={2}>
          <Box>
            <Button
              variant='outlined'
              size='medium'
              disabled={saving}
              onClick={() => onSubmit(formValues, true)}
            >
              Save and schedule more
            </Button>
          </Box>
          <Box>
            <Button
              variant='contained'
              size='medium'
              disabled={saving}
              onClick={() => onSubmit(formValues)}
            >
              Save
            </Button>
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

const NewSessionFormContainer = props => {
  const user = useUser();
  const [patientId, setPatientId] = useState(props?.patient?.id);
  const providerId = user?.provider?.id;
  const sessionTypeQuery = useGetSessionTypesQuery({providerId}, {skip: !providerId});
  const casesQuery = useGetCasesQuery({patient_id: patientId}, {skip: !patientId});
  const sessionTypes = (sessionTypeQuery.data || []);
  const cases = (get(casesQuery, 'data.data') || []).filter(c => c.status === 'ACTIVE');
  const defaultCase = getDefaultCase(cases, {providerId});
  const childProps = {
    ...props,
    defaultCase,
    user,
    providerId,
    sessionTypes,
    onPatientIdChange: (e, id) => setPatientId(id),
  };
  if (casesQuery.isLoading) return <LoadingScreen />;
  return <NewSessionForm {...childProps} />;
};

export default NewSessionFormContainer;